import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { type ClinicMember, useClinicMembers } from '@/lib/useClinicMembers';
import { Loader2, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';

const SCRIBE_APP = 'scribe';
const MEMBER_ROLE = 'MEMBER';

const scribeGrant = (member: ClinicMember) => member.app_grants.find((grant) => grant.app === SCRIBE_APP);

const ClinicMembers = () => {
  const { authAccountId } = useAuth();
  const { toast } = useToast();
  const { members, invites, isLoading, error, invite, removeMember, revokeInvite } = useClinicMembers(true);

  const [email, setEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const handleInvite = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      return;
    }
    setIsInviting(true);
    try {
      const result = await invite(trimmed);
      setEmail('');
      if (result.email_sent) {
        toast({ title: 'Boð sent', description: `Boð var sent á ${trimmed}.` });
      } else {
        toast({
          title: 'Boð búið til',
          description: result.invite_url
            ? `Tölvupóstur sendist ekki — afritaðu hlekkinn: ${result.invite_url}`
            : 'Boð búið til en tölvupóstur sendist ekki.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Villa',
        description: err instanceof Error ? err.message : 'Ekki tókst að senda boð.',
        variant: 'destructive',
      });
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemove = async (accountId: string) => {
    setPendingId(accountId);
    try {
      await removeMember(accountId);
      toast({ title: 'Notandi fjarlægður' });
    } catch (err) {
      toast({
        title: 'Villa',
        description: err instanceof Error ? err.message : 'Ekki tókst að fjarlægja notanda.',
        variant: 'destructive',
      });
    } finally {
      setPendingId(null);
    }
  };

  const handleRevoke = async (inviteId: string) => {
    setPendingId(inviteId);
    try {
      await revokeInvite(inviteId);
      toast({ title: 'Boð afturkallað' });
    } catch (err) {
      toast({
        title: 'Villa',
        description: err instanceof Error ? err.message : 'Ekki tókst að afturkalla boð.',
        variant: 'destructive',
      });
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <CardTitle>Notendur</CardTitle>
        </div>
        <CardDescription>Bjóddu notendum á stöðina, sjáðu virka notendur og afturkallaðu boð.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Invite form */}
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <Label htmlFor="invite-email">Netfang</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="notandi@stöð.is"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={isInviting}
            />
          </div>
          <Button onClick={handleInvite} disabled={isInviting || !email.trim()}>
            {isInviting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
            Senda boð
          </Button>
        </div>

        {error ? (
          <p className="text-destructive text-sm">{error}</p>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Active members */}
            <div className="space-y-2">
              <h3 className="font-medium text-sm">Virkir notendur</h3>
              {members.length === 0 ? (
                <p className="text-muted-foreground text-sm">Engir notendur fundust.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nafn</TableHead>
                      <TableHead>Kennitala</TableHead>
                      <TableHead>Hlutverk</TableHead>
                      <TableHead className="text-right">Aðgerð</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.map((member) => {
                      const grant = scribeGrant(member);
                      const isSelf = member.account_id === authAccountId;
                      const removable = grant?.role === MEMBER_ROLE && !isSelf;
                      return (
                        <TableRow key={member.account_id}>
                          <TableCell>{member.name}</TableCell>
                          <TableCell>{member.kennitala ?? '—'}</TableCell>
                          <TableCell>{grant?.role ?? '—'}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!removable || pendingId === member.account_id}
                              onClick={() => handleRemove(member.account_id)}
                            >
                              {pendingId === member.account_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Fjarlægja'
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Pending invites */}
            {invites.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-medium text-sm">Boð í bið</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Netfang</TableHead>
                      <TableHead>Staða</TableHead>
                      <TableHead>Rennur út</TableHead>
                      <TableHead className="text-right">Aðgerð</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invites.map((pending) => (
                      <TableRow key={pending.id}>
                        <TableCell>{pending.email}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{pending.status}</Badge>
                        </TableCell>
                        <TableCell>
                          {pending.expires_at ? new Date(pending.expires_at).toLocaleDateString('is-IS') : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={pendingId === pending.id}
                            onClick={() => handleRevoke(pending.id)}
                          >
                            {pendingId === pending.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Afturkalla'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ClinicMembers;
